// O gráfico mensal da área de trabalho de desembolso, compartilhado pelas duas políticas
// (a curva Ampli e o cronograma de desembolso do Sienge): barras por competência, com a
// SELEÇÃO DE MESES (design D2). Clicar numa barra marca/desmarca o mês; a seleção vive na
// moldura (`DisbursementWorkspace`) e chega aqui por props.
//
// Selecionar também é possível pela lista de meses (um `details` com caixas de marcação):
// o clique numa barra do canvas não é alcançável por teclado nem por leitor de tela, e a
// lista é a alternativa que faz a mesma coisa.
//
// Os callbacks do chart.js (ticks/tooltip/clique) rodam FORA do render do React, então
// nada aqui chama hook de formatação (`formatNumberToCurrency` lê o contexto do projeto):
// o formatador é puro, a série já vem em reais.
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";

import { competenceOfLabel, labelOfCompetence } from "./months";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export const formatReais = (value) =>
  (Number(value) || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const SELECTED_COLOR = "#1D4ED8";
const FADED_COLOR = "#BFD3F8";

/** Cores por barra do conjunto selecionável: o mês escolhido destaca, os outros esmaecem. */
const barColors = (labels, selectedMonths, base) => {
  if (!(selectedMonths instanceof Set) || selectedMonths.size === 0) return base;
  return labels.map((label) =>
    selectedMonths.has(competenceOfLabel(label)) ? SELECTED_COLOR : FADED_COLOR,
  );
};

const MonthlyBarChart = ({
  labels,
  datasets,
  ariaLabel,
  showLegend = false,
  selectedMonths = null,
  onToggleMonth = null,
  onClearMonths = null,
}) => {
  const selectable = typeof onToggleMonth === "function";
  const selection = selectedMonths instanceof Set ? selectedMonths : new Set();

  const data = {
    labels,
    datasets: datasets.map((dataset) => {
      if (!dataset.selectable) return dataset;
      const { selectable: _selectable, ...rest } = dataset;
      return {
        ...rest,
        backgroundColor: barColors(labels, selection, dataset.backgroundColor),
      };
    }),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    ...(selectable
      ? {
          onClick: (_event, elements) => {
            const element = elements?.[0];
            if (!element) return;
            const competence = competenceOfLabel(labels[element.index]);
            if (competence) onToggleMonth(competence);
          },
        }
      : {}),
    plugins: {
      // A plataforma registra `chartjs-plugin-datalabels` globalmente — sem isto ele
      // desenha o valor cru por cima de cada barra, sobrepondo o eixo e os rótulos.
      datalabels: { display: false },
      legend: { display: showLegend },
      tooltip: {
        callbacks: {
          label: (context) => formatReais(context.parsed.y),
        },
      },
    },
    scales: {
      y: {
        ticks: { callback: (value) => formatReais(value) },
      },
    },
  };

  const selectedLabels = [...selection]
    .sort()
    .map((competence) => labelOfCompetence(competence))
    .filter(Boolean);

  return (
    <div className="ampli-monthly-chart">
      <div className="ampli-curve-chart__canvas">
        <Bar data={data} options={options} aria-label={ariaLabel} />
      </div>
      {selectable ? (
        <div className="ampli-monthly-chart__selection">
          {selectedLabels.length > 0 ? (
            <p role="status">
              {`Meses selecionados: ${selectedLabels.join(", ")}`}
              <button
                type="button"
                className="button button-outline small"
                onClick={() => (onClearMonths
                  ? onClearMonths()
                  : selectedLabels.forEach((label) => onToggleMonth(competenceOfLabel(label))))}
              >
                Limpar seleção
              </button>
            </p>
          ) : (
            <p className="card-label">
              Clique nas barras para selecionar meses e ver os itens que compõem o desembolso.
            </p>
          )}
          <details>
            <summary>Escolher meses pela lista</summary>
            <ul className="ampli-monthly-chart__months">
              {labels.map((label) => {
                const competence = competenceOfLabel(label);
                return (
                  <li key={label}>
                    <label>
                      <input
                        type="checkbox"
                        checked={selection.has(competence)}
                        onChange={() => onToggleMonth(competence)}
                      />
                      <span>{label}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </details>
        </div>
      ) : null}
    </div>
  );
};

export default MonthlyBarChart;
